import { useEffect, useRef, useState, type ReactNode } from "react";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Show } from "shared/components";
import { cx } from "shared/utils/classnames";
import type { ComposerTab } from "./constants";
import { ComposerNarrowProvider } from "../context/ComposerNarrowContext";

interface ComposerFooterRowProps {
  tabs: Tab<ComposerTab>[];
  active: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  showTabs?: boolean;
  leftExtra?: ReactNode;
  children?: ReactNode;
}

const ICON_ONLY_TABS_BELOW = 860;

const TAB_BASE_CLASS =
  "[&_button]:rounded-full [&_button]:h-10 [&_button]:px-4 [&_button]:py-0 [&_button[aria-selected=false]]:text-content-300";

const TAB_ICON_ONLY_CLASS = "[&_button>span]:hidden [&_button]:px-3";

const useIsNarrow = (threshold: number) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < threshold);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isNarrow };
};

export const ComposerFooterRow = ({
  tabs,
  active,
  onTabChange,
  showTabs = true,
  leftExtra,
  children
}: ComposerFooterRowProps) => {
  const { ref, isNarrow } = useIsNarrow(ICON_ONLY_TABS_BELOW);

  return (
    <div
      ref={ref}
      className="flex min-h-[56px] flex-row flex-nowrap items-center gap-4 px-6 py-2"
    >
      <Show if={showTabs || !!leftExtra}>
        <div className="flex min-w-0 shrink-0 items-center gap-4">
          <Show if={showTabs}>
            <Tabs
              tabs={tabs}
              active={active}
              onChange={onTabChange}
              tabStyle="ghost-pink"
              size="sm"
              fullWidthTabs={false}
              className={cx(
                "flex-nowrap",
                TAB_BASE_CLASS,
                isNarrow && TAB_ICON_ONLY_CLASS
              )}
            />
          </Show>
          {leftExtra}
        </div>
      </Show>
      <div className="ml-auto flex min-w-0 shrink items-center">
        <ComposerNarrowProvider isNarrow={isNarrow}>
          {children}
        </ComposerNarrowProvider>
      </div>
    </div>
  );
};
